import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './AnnotationPage.css';
import InfoTooltip from "./InfoTooltip";
import Tutorial from './Tutorial';

const logAnnotationAction = async (pid, poemId, actionType, fieldName, oldValue, newValue, annotationState) => {
    try {
        await fetch('/api/log/annotation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pid,
                poem_id: poemId,
                action_type: actionType,
                field_name: fieldName,
                old_value: oldValue,
                new_value: newValue,
                annotation_state: annotationState
            })
        });
    } catch (error) {
        console.error('Error logging annotation action:', error);
    }
};

export default function AnnotationPage() {
    const { poemId } = useParams();
    const navigate = useNavigate();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const pid = params.get('pid') || 'unknown';
    const [showTutorial, setShowTutorial] = useState(false);

    const withSearch = (extra = {}) => {
        const sp = new URLSearchParams(search || '');
        Object.entries(extra).forEach(([k, v]) => {
            if (v === undefined || v === null) sp.delete(k);
            else sp.set(k, String(v));
        });
        return `?${sp.toString()}`;
    };

    // Poem details fetched from backend -- new version
    // const [poem, setPoem] = useState({ content: '' });

    // Poem details fetched from backend -- old version
    const [poem, setPoem] = useState({ title: '', content: '' });

    // Annotation form state
    const [annotation, setAnnotation] = useState({
        evaluator_id: pid || 'unknown',
        mental_image: 3,
        flow: 3,
        intended_mood: 3,
        originality: 3,
        assumed_theme: '',
    });

    useEffect(() => {
        setAnnotation((prev) => ({ ...prev, evaluator_id: pid || 'unknown' }));
    }, [pid]);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const assumedThemeOptions = [
        'Joyful','Melancholy','Energetic','Inspirational',
        'Love','Calm','Political','Sarcastic','Fearful'
    ];

    //restore state
    useEffect(() => {
        const saved = sessionStorage.getItem('annotationState');
        if (saved) {
            setAnnotation(JSON.parse(saved));
            sessionStorage.removeItem('annotationState');
        }
    }, []);

    // Fetch poem details on mount
    // useEffect(() => {
    //     fetch(`http://127.0.0.1:5001/api/tagged_poem/${poemId}`)
    //     fetch(`https://140.238.156.35:5001/api/tagged_poem/${poemId}`)
    //     fetch(`https://collapoet.ddns.net/api/tagged_poem/${poemId}`)
    //
    //         .then((res) => res.json())
    //         .then(setPoem)
    //         .catch(console.error);
    // }, [poemId]);
    useEffect(() => {
        fetch(`/api/tagged_poem/${poemId}`)
            .then((res) => res.json())
            .then(setPoem)
            .catch(console.error);
    }, [poemId]);

    useEffect(() => {
        if (pid && poemId) {
            logAnnotationAction(pid, parseInt(poemId), 'access', null, null, null, annotation);
        }
    }, [pid, poemId]);

    const handleBack = () => {
        navigate(`/poems${search}`);
    };

    const handleLikertChange = (field, value) => {
        const oldValue = annotation[field];
        const newAnnotation = { ...annotation, [field]: value };

        if (pid) {
            logAnnotationAction(pid, parseInt(poemId), 'rating_change', field, oldValue, value, newAnnotation);
        }

        setAnnotation((prev) => ({ ...prev, [field]: value }));
    };

    const toggleDropdown = () => setDropdownOpen((o) => !o);
    const handleAssumedThemeChange = (theme) => {
        const oldValue = annotation.assumed_theme;
        const newAnnotation = { ...annotation, assumed_theme: theme };

        if (pid) {
            logAnnotationAction(pid, parseInt(poemId), 'theme_select', 'assumed_theme', oldValue, theme, newAnnotation);
        }

        setAnnotation((prev) => ({ ...prev, assumed_theme: theme }));
        setDropdownOpen(false);
    };

    // const handleEvaluatorIdChange = (e) => {
    //     const newValue = e.target.value;
    //     const oldValue = annotation.evaluator_id;
    //     const newAnnotation = { ...annotation, evaluator_id: newValue };
    //
    //     if (pid && oldValue !== newValue) {
    //         logAnnotationAction(pid, parseInt(poemId), 'evaluator_change', 'evaluator_id', oldValue, newValue, newAnnotation);
    //     }
    //
    //     setAnnotation(newAnnotation);
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            poem_id: Number(poemId),
            evaluator_id: pid || 'unknown',
            mental_image: annotation.mental_image,
            flow: annotation.flow,
            intended_mood: annotation.intended_mood,
            originality: annotation.originality,
            assumed_theme: annotation.assumed_theme,
            pid: pid || 'unknown'
        };

        try {
            const r = await fetch('/api/annotations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const d = await r.json().catch(() => ({}));

            if (!r.ok) {
                // Backend should return errors like:
                // 409: already annotated, 403: own poem
                alert(d.error || 'Could not submit annotation.');
                return;
            }

            //  go back to poems list (keep pid/interface query params)
            navigate(`/poems${search}`);
        } catch (err) {
            console.error(err);
            alert('Server error. Please try again.');
        }
    };

    // Label/dialogue arrays
    const mentalImageLabels = ['Very Poorly','Poorly','Neutral','Well','Very Well'];
    const mentalImageDialogues = [
        "I can't visualize anything at all.",
        "Only vague or partial images.",
        "Some images come to mind, but not strongly.",
        "Clear and fairly detailed images.",
        "Extremely vivid; I can see it clearly."
    ];
    const flowLabels = ['Very Bad','Bad','Neutral','Good','Very Good'];
    const flowDialogues = [
        "Very difficult to read; feels jarring or disjointed.",
        "Somewhat difficult to follow; has awkward transitions.",
        "Average flow; neither smooth nor difficult.",
        "Reads smoothly with good transitions.",
        "Exceptionally smooth reading experience; perfect rhythm."
    ];
    const intendedMoodLabels = ['Very Off','Off','Neutral','On','Very On'];
    const intendedMoodDialogues = [
        "Completely misses the intended emotional tone.",
        "Somewhat misaligned with the intended mood.",
        "Moderately aligned with the intended mood.",
        "Well aligned with the intended emotional tone.",
        "Perfectly captures the intended mood and emotion."
    ];
    const originalityLabels = ['Very Unoriginal','Unoriginal','Neutral','Original','Very Original'];
    const originalityDialogues = [
        "Very derivative; feels like I've seen it many times before.",
        "Somewhat conventional with few original elements.",
        "Moderately original with some fresh elements.",
        "Quite original with fresh perspective or approach.",
        "Exceptionally unique and innovative."
    ];

    return (
        <div className="annotation-page">
            {/*<button onClick={() => navigate('/')} className="back-button-annotate">*/}
            {/*    ← Back to Poems*/}
            {/*</button>*/}
            <button
                onClick={handleBack}
                className="back-button-annotate"
            >
                ← Back to Poems
            </button>
            <button
                onClick={() => {
                    sessionStorage.setItem(
                        'annotationState',
                        JSON.stringify(annotation)
                    );
                    setShowTutorial(true);
                }}
                className="tutorial-button"
            >
                Tutorial
            </button>
            {showTutorial && (
                <Tutorial
                    inline
                    onClose={() => setShowTutorial(false)}
                    mode="annotate"
                    type="annotation"
                />
            )}

            <div className="annotation-content">
                {/* Poem Display */}
                <div className="poem-column">
                    {/*<h2>{poem.title}</h2>*/}
                    <p className="poem-text">{poem.content}</p>
                </div>

                {/* Annotation Form */}
                <div className="form-column">
                    <form onSubmit={handleSubmit}>
                        {/* Evaluator ID */}
                        {/*<div className="metric-section">*/}
                        {/*    <label className="input-label" htmlFor="evaluator_id">*/}
                        {/*        Evaluator ID:*/}
                        {/*    </label>*/}
                        {/*    <input*/}
                        {/*        id="evaluator_id"*/}
                        {/*        className="text-input"*/}
                        {/*        value={annotation.evaluator_id}*/}
                        {/*        onChange={handleEvaluatorIdChange}*/}
                        {/*        placeholder="Enter evaluator ID"*/}
                        {/*    />*/}
                        {/*</div>*/}

                        {/* Mental Image */}
                        <div className="metric-section">
                            <span className="metric-title">Mental Image</span>
                            <InfoTooltip>
                                Measures how vividly the poem paints pictures in your mind.
                            </InfoTooltip>
                            <p>How well does the poem create mental images?</p>
                            <div className="button-group">
                                {mentalImageLabels.map((lab, i) => (
                                    <button
                                        key={lab}
                                        type="button"
                                        className={`rating-button ${
                                            annotation.mental_image === i + 1 ? 'selected' : ''
                                        }`}
                                        onClick={() => handleLikertChange('mental_image', i + 1)}
                                    >
                                        {lab}
                                    </button>
                                ))}
                            </div>
                            <div className="dialogue-container">
                                <p>
                  <span className="dialogue-label">
                    {mentalImageLabels[annotation.mental_image - 1]}:
                  </span>{' '}
                                    {mentalImageDialogues[annotation.mental_image - 1]}
                                </p>
                            </div>
                        </div>

                        {/* Flow */}
                        <div className="metric-section">
                            <span className="metric-title">Flow</span>
                            <InfoTooltip>
                                Assesses how smoothly the poem’s rhythm and pacing flow aloud.
                            </InfoTooltip>
                            <p>How smoothly could the poem be read?</p>
                            <div className="button-group">
                                {flowLabels.map((lab, i) => (
                                    <button
                                        key={lab}
                                        type="button"
                                        className={`rating-button ${
                                            annotation.flow === i + 1 ? 'selected' : ''
                                        }`}
                                        onClick={() => handleLikertChange('flow', i + 1)}
                                    >
                                        {lab}
                                    </button>
                                ))}
                            </div>
                            <div className="dialogue-container">
                                <p>
                  <span className="dialogue-label">
                    {flowLabels[annotation.flow - 1]}:
                  </span>{' '}
                                    {flowDialogues[annotation.flow - 1]}
                                </p>
                            </div>
                        </div>

                        {/* Assumed Theme */}
                        <div className="metric-section">
                            <span className="metric-title">Assumed Theme</span>
                            <InfoTooltip>
                                What is the main theme of the poem from your understanding?
                            </InfoTooltip>
                            <p>Please select the theme you assume for this poem:</p>
                            <div className="dropdown-container">
                                <div className="dropdown-select" onClick={toggleDropdown}>
                                    {annotation.assumed_theme || 'Select Theme'} <span>▼</span>
                                </div>
                                {dropdownOpen && (
                                    <div className="dropdown-options">
                                        {assumedThemeOptions.map((opt) => (
                                            <div
                                                key={opt}
                                                className="dropdown-option"
                                                onClick={() => handleAssumedThemeChange(opt)}
                                            >
                                                {opt}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Intended Mood */}
                        <div className="metric-section">
                            <span className="metric-title">Intended Mood</span>
                            <InfoTooltip>
                                How clearly the poem conveys the chosen emotion based on your assumed theme.
                            </InfoTooltip>
                            <p>How aligned is it with the assumed mood?</p>
                            <div className="button-group">
                                {intendedMoodLabels.map((lab, i) => (
                                    <button
                                        key={lab}
                                        type="button"
                                        className={`rating-button ${
                                            annotation.intended_mood === i + 1 ? 'selected' : ''
                                        }`}
                                        onClick={() => handleLikertChange('intended_mood', i + 1)}
                                    >
                                        {lab}
                                    </button>
                                ))}
                            </div>
                            <div className="dialogue-container">
                                <p>
                  <span className="dialogue-label">
                    {intendedMoodLabels[annotation.intended_mood - 1]}:
                  </span>{' '}
                                    {intendedMoodDialogues[annotation.intended_mood - 1]}
                                </p>
                            </div>
                        </div>

                        {/* Originality */}
                        <div className="metric-section">
                            <span className="metric-title">Originality</span>
                            <InfoTooltip>
                                Judges how fresh and unique the phrasing and imagery are.
                            </InfoTooltip>
                            <p>How unique does the poem feel?</p>
                            <div className="button-group">
                                {originalityLabels.map((lab, i) => (
                                    <button
                                        key={lab}
                                        type="button"
                                        className={`rating-button ${
                                            annotation.originality === i + 1 ? 'selected' : ''
                                        }`}
                                        onClick={() => handleLikertChange('originality', i + 1)}
                                    >
                                        {lab}
                                    </button>
                                ))}
                            </div>
                            <div className="dialogue-container">
                                <p>
                  <span className="dialogue-label">
                    {originalityLabels[annotation.originality - 1]}:
                  </span>{' '}
                                    {originalityDialogues[annotation.originality - 1]}
                                </p>
                            </div>
                        </div>

                        <button type="submit" className="submit-button">
                            Submit Annotation
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
